/**
 * BirthChartPage — Vedic birth chart with two views:
 *   Summary   — plain-language interpretation, default for all users
 *   Detailed  — practitioner chart (D3 North Indian diagram, tables, divisionals)
 *
 * View preference is persisted to localStorage so practitioners keep their mode.
 */
import { useRef, useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import * as d3 from 'd3'
import { chartApi, isNotFoundError, type BirthChartFull, type PlanetPosition, type ChartInterpretation, type VedAstroPlanetEnrichment, type VedAstroEnrichment } from '@/lib/api'
import { cn } from '@/lib/utils'
import { Star, RefreshCw, BookOpen, Sparkles, ChevronDown, ChevronUp, LayoutList, BarChart3 } from 'lucide-react'
import { useTheme } from '@/store'
import { ErrorState, SetupPrompt, SkeletonBirthChart } from '@/components/ui'
import { Card } from '@/components/Card'
import { Button } from '@/components/Button'

// ── Constants ────────────────────────────────────────────────────────────────

const SIGNS    = ['Aries','Taurus','Gemini','Cancer','Leo','Virgo','Libra','Scorpio','Sagittarius','Capricorn','Aquarius','Pisces']
const SIGNS_SA = ['मेष','वृष','मिथुन','कर्क','सिंह','कन्या','तुला','वृश्चिक','धनु','मकर','कुम्भ','मीन']

const PLANETS_ABBR: Record<string, string> = {
  Sun: 'Su', Moon: 'Mo', Mars: 'Ma', Mercury: 'Me', Jupiter: 'Ju',
  Venus: 'Ve', Saturn: 'Sa', Rahu: 'Ra', Ketu: 'Ke', Ascendant: 'As',
}

const PLANET_COLORS: Record<string, string> = {
  Sun: '#f59e0b', Moon: '#c8c8f0', Mars: '#ef4444', Mercury: '#22c55e',
  Jupiter: '#f59e0b', Venus: '#f472b6', Saturn: '#94a3b8', Rahu: '#a78bfa', Ketu: '#fb923c',
  Ascendant: '#fbbf24',
}

// Unicode glyphs for each graha
const PLANET_ICONS: Record<string, string> = {
  Sun: '☉', Moon: '☽', Mars: '♂', Mercury: '☿', Jupiter: '♃',
  Venus: '♀', Saturn: '♄', Rahu: '☊', Ketu: '☋', Ascendant: '↑',
}

// Benefic/malefic classification for color-coding
const BENEFIC = new Set(['Jupiter', 'Venus', 'Mercury', 'Moon'])
const MALEFIC = new Set(['Saturn', 'Mars', 'Sun', 'Rahu', 'Ketu'])

function planetNatureClass(planet: string) {
  if (BENEFIC.has(planet)) return 'text-auspicious'
  if (MALEFIC.has(planet)) return 'text-gold'
  return 'text-star-soft'
}

// North Indian 4×4 grid layout — 12 houses, clockwise from top-centre
const NI_CELLS: Array<{ row: number; col: number }> = [
  { row: 0, col: 1 }, // H1
  { row: 0, col: 2 }, // H2
  { row: 0, col: 3 }, // H3
  { row: 1, col: 3 }, // H4
  { row: 2, col: 3 }, // H5
  { row: 3, col: 3 }, // H6
  { row: 3, col: 2 }, // H7
  { row: 3, col: 1 }, // H8
  { row: 3, col: 0 }, // H9
  { row: 2, col: 0 }, // H10
  { row: 1, col: 0 }, // H11
  { row: 0, col: 0 }, // H12
]

// ── (Skeleton loaders delegated to ui.tsx SkeletonBirthChart) ────────────────

function SummarySkeleton() {
  return <SkeletonBirthChart className="mt-2" />
}

// ── Summary panel ─────────────────────────────────────────────────────────────

interface SummaryPanelProps {
  interp: ChartInterpretation | undefined
  isLoading: boolean
  chart: BirthChartFull
  enrichment?: VedAstroEnrichment | null
}

function SummaryPanel({ interp, isLoading, chart, enrichment }: SummaryPanelProps) {
  const enrichMap = new Map<string, VedAstroPlanetEnrichment>(
    (enrichment?.planets ?? []).map(e => [e.graha, e]),
  )
  const [lang, setLang] = useState<'en' | 'hi'>('en')
  const [yogasOpen, setYogasOpen] = useState(false)

  if (isLoading) return <SummarySkeleton />
  if (!interp) return (
    <p className="text-star-dim text-sm text-center py-8">Interpretation unavailable. Please reload.</p>
  )

  const data = interp
  const t = (b: { en: string; hi: string }) => b[lang]

  const pillars = [
    { label: 'Ascendant (Lagna)', sanskrit: 'लग्न',       sign: chart.lagnaSign, icon: '↑', text: data.lagna,     planet: 'Ascendant' },
    { label: 'Moon Sign',         sanskrit: 'चंद्र राशि', sign: chart.moonSign,  icon: '☽', text: data.moonSign,  planet: 'Moon' },
    { label: 'Sun Sign',          sanskrit: 'सूर्य राशि', sign: chart.sunSign,   icon: '☉', text: data.sunSign,   planet: 'Sun' },
  ]

  return (
    <section className="space-y-6" aria-label="Birth chart summary">

      {/* Language toggle */}
      <div className="flex items-center justify-between">
        <p className="overline">Plain-language reading</p>
        <div className="flex items-center gap-1 p-0.5 rounded-lg bg-cosmic-dusk ring-1 ring-white/5" role="group" aria-label="Select language">
          {(['en', 'hi'] as const).map(l => (
            <button
              key={l}
              onClick={() => setLang(l)}
              aria-pressed={lang === l}
              className={cn(
                'px-2.5 py-1 rounded-md text-2xs font-mono transition-all duration-fast',
                lang === l
                  ? 'bg-violet/20 text-violet-glow shadow-card'
                  : 'text-star-dim hover:text-star-bright',
              )}
            >
              {l === 'en' ? 'English' : 'हिंदी'}
            </button>
          ))}
        </div>
      </div>

      {/* Overview bullets */}
      {data.overview.length > 0 && (
        <div
          className="p-5 rounded-xl bg-zinc-900/60 border border-zinc-800"
          aria-labelledby="overview-heading"
        >
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-4 h-4 text-gold" aria-hidden="true" />
            <h2 id="overview-heading" className="text-star-bright text-sm font-semibold">
              Your Chart at a Glance
            </h2>
          </div>
          <ul className="space-y-2.5" role="list">
            {data.overview.map((line, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span className="mt-1.5 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-gold" aria-hidden="true" />
                <p className={cn('text-sm leading-relaxed', lang === 'hi' ? 'font-display text-star-soft' : 'text-star-soft')}>
                  {t(line)}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Three pillars */}
      <div>
        <h2 className="overline mb-3">Your Three Pillars</h2>
        <div className="grid sm:grid-cols-3 gap-3">
          {pillars.map(({ label, sanskrit, sign, icon, text, planet }) => (
            <div
              key={label}
              className="p-4 rounded-xl bg-zinc-900/50 border border-zinc-800 space-y-2"
              aria-label={label}
            >
              {/* Header */}
              <div className="flex items-center gap-2">
                <span
                  className="text-lg leading-none"
                  aria-hidden="true"
                  style={{ color: PLANET_COLORS[planet] ?? '#c8c8f0' }}
                >
                  {icon}
                </span>
                <div>
                  <p className="text-star-bright text-xs font-semibold leading-tight">{label}</p>
                  <p className="text-star-dim text-2xs font-display">{sanskrit}</p>
                </div>
              </div>
              {/* Sign badge */}
              <span
                className={cn(
                  'inline-block px-2 py-0.5 rounded-full text-2xs font-semibold border',
                  BENEFIC.has(planet) ? 'bg-auspicious/10 text-auspicious border-auspicious/20' : 'bg-gold/10 text-gold border-gold/20',
                )}
              >
                {sign}
              </span>
              {/* Description */}
              <p className={cn('text-xs leading-relaxed', lang === 'hi' ? 'font-display text-star-soft' : 'text-star-soft')}>
                {t(text)}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Moon Nakshatra personality */}
      <div
        className="p-5 rounded-xl bg-violet-deep/20 border border-violet/20"
        aria-labelledby="nakshatra-heading"
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-violet-deep/60 flex items-center justify-center flex-shrink-0" aria-hidden="true">
            <span className="text-lg">🌙</span>
          </div>
          <div className="flex-1 min-w-0">
            <h2 id="nakshatra-heading" className="text-star-bright text-sm font-semibold mb-0.5">
              Moon Nakshatra: <span className="text-gold">{chart.nakshatra}</span>
              <span className="text-star-dim text-2xs font-mono ml-1.5">Pada {chart.pada}</span>
            </h2>
            <p className="text-star-dim text-2xs mb-2">{chart.moonSign} Moon · {chart.nakshatra}</p>
            <p className={cn('text-sm leading-relaxed', lang === 'hi' ? 'font-display text-star-soft' : 'text-star-soft')}>
              {t(data.moonNakshatra)}
            </p>
          </div>
        </div>
      </div>

      {/* Key yogas — collapsible */}
      {data.keyYogas.length > 0 && (
        <div aria-labelledby="yogas-heading">
          <button
            onClick={() => setYogasOpen(v => !v)}
            aria-expanded={yogasOpen}
            aria-controls="yogas-list"
            className="w-full flex items-center justify-between text-left mb-3 group"
          >
            <h2 id="yogas-heading" className="text-star-dim text-2xs font-mono uppercase tracking-wider group-hover:text-star-soft transition-colors">
              Key Yogas Detected <span className="text-violet-glow">({data.keyYogas.length})</span>
            </h2>
            {yogasOpen
              ? <ChevronUp className="w-3.5 h-3.5 text-star-dim" aria-hidden="true" />
              : <ChevronDown className="w-3.5 h-3.5 text-star-dim" aria-hidden="true" />
            }
          </button>

          {yogasOpen && (
            <div id="yogas-list" className="grid sm:grid-cols-2 gap-2">
              {data.keyYogas.map((yoga) => (
                <div key={yoga.name} className="p-4 rounded-xl bg-zinc-900/60 border border-violet/20">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <p className="text-violet-glow text-sm font-semibold">{yoga.name}</p>
                    <div className="flex gap-1 flex-wrap justify-end">
                      {yoga.planets.map(p => (
                        <span
                          key={p}
                          className={cn('text-2xs font-mono px-1.5 py-0.5 rounded border', planetNatureClass(p),
                            BENEFIC.has(p) ? 'bg-auspicious/10 border-auspicious/20' : 'bg-gold/10 border-gold/20',
                          )}
                        >
                          {PLANET_ICONS[p] ?? ''} {p}
                        </span>
                      ))}
                    </div>
                  </div>
                  <p className={cn('text-xs leading-relaxed', lang === 'hi' ? 'font-display text-star-soft' : 'text-star-soft')}>
                    {t(yoga.description)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Planets in houses */}
      {data.planetInHouse.length > 0 && (
        <div aria-labelledby="planets-heading">
          <h2 id="planets-heading" className="overline mb-3">
            Planets in Houses
          </h2>
          <div className="space-y-2" role="list">
            {data.planetInHouse.map((p) => {
              const e = enrichMap.get(p.graha)
              const dignity = e ? dignityLabel(e) : null
              return (
              <div
                key={`${p.graha}-${p.house}`}
                role="listitem"
                className="flex items-start gap-3 p-3 rounded-lg bg-zinc-900/50 border border-zinc-800"
              >
                {/* Planet glyph + name + enrichment badges */}
                <div className="flex-shrink-0 min-w-[90px] flex flex-col gap-1" aria-label={`${p.graha} in house ${p.house}`}>
                  <div className="flex items-center gap-1.5">
                    <span
                      className={cn('text-base leading-none', planetNatureClass(p.graha))}
                      aria-hidden="true"
                    >
                      {PLANET_ICONS[p.graha] ?? ''}
                    </span>
                    <span className={cn('text-sm font-semibold', planetNatureClass(p.graha))}>
                      {p.graha}
                    </span>
                    <span className="text-star-dim text-2xs font-mono">H{p.house}</span>
                  </div>
                  {e && (
                    <div className="flex flex-wrap gap-1">
                      {dignity && (
                        <span className={cn('text-2xs font-mono px-1.5 py-0.5 rounded border', dignityClasses(e))}>
                          {dignity}
                        </span>
                      )}
                      <AshtakavargaBar score={e.ashtakavargaScore} />
                    </div>
                  )}
                </div>
                {/* Description */}
                <p className={cn('text-xs leading-relaxed flex-1', lang === 'hi' ? 'font-display text-star-soft' : 'text-star-soft')}>
                  {t(p.description)}
                </p>
              </div>
              )
            })}
          </div>
        </div>
      )}

      {!interp && (
        <p className="text-center text-star-dim text-2xs py-2">
          Sample interpretation shown. Connect your birth details for a personalised reading.
        </p>
      )}
    </section>
  )
}

// ── Vedic Chart SVG (D3) ─────────────────────────────────────────────────────

interface VedicChartProps {
  planets: PlanetPosition[]
  ascendantSign: string
  size?: number
}

function VedicChart({ planets, ascendantSign, size = 420 }: VedicChartProps) {
  const svgRef = useRef<SVGSVGElement>(null)
  const theme = useTheme()

  useEffect(() => {
    if (!svgRef.current) return
    const ascIdx = SIGNS.indexOf(ascendantSign)
    if (ascIdx < 0) return

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()

    const cellSize = size / 4
    const pad = 4
    const g = svg.append('g')

    // Add glow filter for planet nodes
    const defs = svg.append('defs')
    const filter = defs.append('filter').attr('id', 'planet-glow')
    filter.append('feGaussianBlur').attr('stdDeviation', '2').attr('result', 'blur')
    filter.append('feComposite').attr('in', 'SourceGraphic').attr('in2', 'blur').attr('operator', 'over')

    NI_CELLS.forEach((cell, houseIdx) => {
      const houseNum = houseIdx + 1
      const signIdx  = (ascIdx + houseIdx) % 12
      const signName = SIGNS[signIdx]
      const signSa   = SIGNS_SA[signIdx]
      const x = cell.col * cellSize
      const y = cell.row * cellSize

      g.append('rect')
        .attr('x', x + 0.5).attr('y', y + 0.5)
        .attr('width', cellSize - 1).attr('height', cellSize - 1)
        .style('fill', 'var(--chart-bg)').style('stroke', 'var(--chart-border)').attr('stroke-width', 1)

      g.append('text')
        .attr('x', x + pad + 2).attr('y', y + pad + 10)
        .style('fill', 'var(--chart-text-dim)').attr('font-size', 9).attr('font-family', 'Geist Mono, monospace')
        .text(houseNum)

      g.append('text')
        .attr('x', x + cellSize - pad - 2).attr('y', y + pad + 10)
        .style('fill', 'var(--chart-text-muted)').attr('font-size', 8).attr('text-anchor', 'end').attr('font-family', 'Geist, sans-serif')
        .text(signName.slice(0, 3))

      g.append('text')
        .attr('x', x + cellSize / 2).attr('y', y + cellSize - pad - 4)
        .style('fill', 'var(--chart-text-sub)').attr('font-size', 9).attr('text-anchor', 'middle').attr('font-family', 'serif')
        .text(signSa)

      const inHouse = planets.filter(p => p.house === houseNum || p.sign === signName)
      inHouse.forEach((p, pi) => {
        const col = pi % 3
        const row = Math.floor(pi / 3)
        const px = x + pad + 14 + col * 24
        const py = y + 24 + row * 16
        const abbr  = PLANETS_ABBR[p.planet] ?? p.planet.slice(0, 2)
        const color = PLANET_COLORS[p.planet] ?? 'var(--chart-text-dim)'

        g.append('text')
          .attr('x', px).attr('y', py)
          .attr('fill', color).attr('font-size', 10).attr('font-weight', 600).attr('font-family', 'Geist, sans-serif')
          .attr('filter', 'url(#planet-glow)')
          .text(p.isRetrograde ? `${abbr}ᴿ` : abbr)
          .append('title').text(`${p.planet} ${p.isRetrograde ? '(R)' : ''} ${p.degrees.toFixed(1)}° ${p.sign}`)
      })
    })

    const cellSize3 = cellSize * 3
    g.append('line').attr('x1', cellSize).attr('y1', cellSize).attr('x2', cellSize3).attr('y2', cellSize3).style('stroke', 'var(--chart-border)').attr('stroke-width', 1)
    g.append('line').attr('x1', cellSize3).attr('y1', cellSize).attr('x2', cellSize).attr('y2', cellSize3).style('stroke', 'var(--chart-border)').attr('stroke-width', 1)

    const lagnaCell = NI_CELLS[0]
    g.append('line')
      .attr('x1', lagnaCell.col * cellSize).attr('y1', lagnaCell.row * cellSize)
      .attr('x2', (lagnaCell.col + 1) * cellSize).attr('y2', (lagnaCell.row + 1) * cellSize)
      .attr('stroke', 'var(--chart-accent)').attr('stroke-width', 1.5).attr('opacity', 0.6)

    g.append('text')
      .attr('x', lagnaCell.col * cellSize + cellSize / 2).attr('y', lagnaCell.row * cellSize + cellSize / 2 + 4)
      .attr('fill', 'var(--chart-accent)').attr('font-size', 8).attr('font-family', 'Geist Mono, monospace')
      .attr('text-anchor', 'middle').attr('opacity', 0.7).text('Lagna')

    g.append('text')
      .attr('x', size / 2).attr('y', size / 2 + 5)
      .style('fill', 'var(--chart-text-sub)').attr('font-size', 14).attr('text-anchor', 'middle').attr('font-family', 'serif')
      .text('✦')
  }, [planets, ascendantSign, size, theme])

  return (
    <svg
      ref={svgRef}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="w-full max-w-[420px] mx-auto"
      aria-label="Vedic birth chart diagram"
      role="img"
    />
  )
}

// ── VedAstro enrichment helpers ───────────────────────────────────────────────

function dignityLabel(e: VedAstroPlanetEnrichment): string | null {
  if (e.isExalted)        return 'Exalted'
  if (e.isInMoolatrikona) return 'Moolatrikona'
  if (e.isInOwnSign)      return 'Own Sign'
  if (e.isDebilitated)    return 'Debilitated'
  return null
}

function dignityClasses(e: VedAstroPlanetEnrichment): string {
  if (e.isExalted || e.isInMoolatrikona || e.isInOwnSign)
    return 'bg-auspicious/10 text-auspicious border-auspicious/30'
  if (e.isDebilitated)
    return 'bg-inauspicious/10 text-inauspicious border-inauspicious/30'
  return 'bg-zinc-800/60 text-star-dim border-zinc-700/40'
}

/** 0–8 bar, colour shifts green→amber→red by score */
function AshtakavargaBar({ score }: { score: number }) {
  const pct = Math.round((score / 8) * 100)
  const color = score >= 5 ? 'bg-auspicious' : score >= 3 ? 'bg-gold' : 'bg-inauspicious'
  return (
    <div className="flex items-center gap-1.5" title={`Ashtakavarga: ${score}/8`}>
      <div className="w-10 h-1.5 rounded-full bg-zinc-800 overflow-hidden flex-shrink-0">
        <div className={cn('h-full rounded-full transition-all', color)} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-2xs font-mono text-star-dim">{score}/8</span>
    </div>
  )
}

// ── Planet table ─────────────────────────────────────────────────────────────

function PlanetTable({ planets, enrichment }: { planets: PlanetPosition[]; enrichment?: VedAstroEnrichment | null }) {
  const enrichMap = new Map<string, VedAstroPlanetEnrichment>(
    (enrichment?.planets ?? []).map(e => [e.graha, e]),
  )
  const hasEnrichment = enrichMap.size > 0

  const headers = hasEnrichment
    ? ['Planet', 'Sign', 'House', 'Degree', 'Nakshatra', 'Dignity', 'Aspects', 'AV', 'R']
    : ['Planet', 'Sign', 'House', 'Degree', 'Nakshatra', 'R']

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" aria-label="Planet positions">
        <thead>
          <tr className="border-b border-zinc-800">
            {headers.map(h => (
              <th key={h} scope="col" className="text-left text-star-dim text-2xs font-mono uppercase tracking-wider py-2 px-3 first:pl-0">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {planets.map((p) => {
            const e = enrichMap.get(p.planet)
            const dignity = e ? dignityLabel(e) : null
            return (
              <tr key={p.planet} className="border-b border-zinc-800/50 hover:bg-zinc-900/30 transition-colors">
                <td className="py-2 px-3 first:pl-0">
                  <span className="font-semibold" style={{ color: PLANET_COLORS[p.planet] ?? '#c8c8f0' }}>
                    {PLANET_ICONS[p.planet] ?? ''} {p.planet}
                  </span>
                </td>
                <td className="py-2 px-3 text-star-soft">{p.sign}</td>
                <td className="py-2 px-3 text-star-muted font-mono">{p.house}</td>
                <td className="py-2 px-3 text-star-muted font-mono">{p.degrees.toFixed(2)}°</td>
                <td className="py-2 px-3 text-star-soft">{p.nakshatra}</td>

                {hasEnrichment && (
                  <>
                    <td className="py-2 px-3">
                      {dignity && (
                        <span className={cn('text-2xs font-mono px-1.5 py-0.5 rounded border', e ? dignityClasses(e) : '')}>
                          {dignity}
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-3">
                      {e && (
                        <span className="flex items-center gap-1">
                          {e.hasGoodAspects && (
                            <span className="text-auspicious text-2xs font-mono" title="Benefic aspects">✦</span>
                          )}
                          {e.hasBadAspects && (
                            <span className="text-inauspicious text-2xs font-mono" title="Malefic aspects">✖</span>
                          )}
                          {e.isAfflicted && (
                            <span className="text-gold text-2xs font-mono" title="Afflicted">⚠</span>
                          )}
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-3">
                      {e && <AshtakavargaBar score={e.ashtakavargaScore} />}
                    </td>
                  </>
                )}

                <td className="py-2 px-3">
                  {p.isRetrograde && <span className="text-inauspicious text-2xs font-mono">R</span>}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      {hasEnrichment && (
        <p className="text-star-dim text-2xs font-mono mt-2 flex items-center gap-2">
          <span className="text-auspicious">✦</span> benefic aspects
          <span className="text-inauspicious ml-2">✖</span> malefic
          <span className="text-gold ml-2">⚠</span> afflicted
          <span className="ml-auto">AV = Ashtakavarga 0–8</span>
        </p>
      )}
    </div>
  )
}

// ── House lords table ─────────────────────────────────────────────────────────

function HouseLordsTable({ chart }: { chart: BirthChartFull }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" aria-label="House lords">
        <thead>
          <tr className="border-b border-zinc-800">
            {['House', 'Sign', 'Lord', 'Lord in'].map(h => (
              <th key={h} scope="col" className="text-left text-star-dim text-2xs font-mono uppercase tracking-wider py-2 px-3 first:pl-0">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(chart.houseLords ?? []).map((hl) => (
            <tr key={hl.house} className="border-b border-zinc-800/50 hover:bg-zinc-900/60/50 transition-colors">
              <td className="py-2 px-3 first:pl-0 text-star-muted font-mono">{hl.house}</td>
              <td className="py-2 px-3 text-star-soft">{hl.sign}</td>
              <td className="py-2 px-3">
                <span className="font-semibold" style={{ color: PLANET_COLORS[hl.lord] ?? '#c8c8f0' }}>
                  {PLANET_ICONS[hl.lord] ?? ''} {hl.lord}
                </span>
              </td>
              <td className="py-2 px-3 text-star-muted font-mono">H{hl.lordHouse}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Detailed tabs ─────────────────────────────────────────────────────────────

type DetailTab = 'chart' | 'planets' | 'houses' | 'D9' | 'D10'

const DETAIL_TABS: Array<{ id: DetailTab; label: string; sanskrit?: string }> = [
  { id: 'chart',   label: 'Birth Chart', sanskrit: 'जन्म कुंडली' },
  { id: 'planets', label: 'Planets',     sanskrit: 'ग्रह' },
  { id: 'houses',  label: 'House Lords', sanskrit: 'भाव' },
  { id: 'D9',      label: 'D9 Navamsa',  sanskrit: 'नवांश' },
  { id: 'D10',     label: 'D10 Dasamsa', sanskrit: 'दशांश' },
]

function DetailedView({ chart, enrichment }: { chart: BirthChartFull; enrichment?: VedAstroEnrichment | null }) {
  const [activeTab, setActiveTab] = useState<DetailTab>('chart')

  return (
    <>
      {/* Sub-tabs */}
      <nav
        className="flex overflow-x-auto gap-1 p-1 rounded-xl bg-zinc-900/50 border border-zinc-800 scrollbar-hide"
        aria-label="Detailed chart views"
        role="tablist"
      >
        {DETAIL_TABS.map(tab => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`detail-panel-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150',
              activeTab === tab.id
                ? 'bg-zinc-800/60 text-star-white shadow-card'
                : 'text-star-muted hover:text-star-bright hover:bg-zinc-900/60',
            )}
          >
            {tab.label}
            {tab.sanskrit && (
              <span className={cn('ml-1.5 text-2xs font-display', activeTab === tab.id ? 'text-gold' : 'text-star-dim')}>
                {tab.sanskrit}
              </span>
            )}
          </button>
        ))}
      </nav>

      {/* Tab panels */}
      <div className="card-cosmic p-5">
        <div id={`detail-panel-chart`} role="tabpanel" aria-labelledby="chart" hidden={activeTab !== 'chart'}>
          {activeTab === 'chart' && (
            <div className="flex flex-col items-center gap-6">
              <VedicChart planets={chart.planets} ascendantSign={chart.ascendant} size={420} />
              <div className="w-full grid grid-cols-3 sm:grid-cols-5 gap-2" aria-label="Planet color legend">
                {Object.entries(PLANET_COLORS).map(([planet, color]) => (
                  <div key={planet} className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} aria-hidden="true" />
                    <span className="text-star-dim text-2xs">{planet}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {activeTab === 'planets' && (
          <div id="detail-panel-planets" role="tabpanel">
            <PlanetTable planets={chart.planets} enrichment={enrichment} />
          </div>
        )}

        {activeTab === 'houses' && (
          <div id="detail-panel-houses" role="tabpanel">
            <HouseLordsTable chart={chart} />
          </div>
        )}

        {(activeTab === 'D9' || activeTab === 'D10') && (() => {
          const div = chart.divisional?.find(d => d.type === activeTab)
          if (!div) return (
            <div role="tabpanel">
              <p className="text-star-muted text-sm text-center py-8">Divisional chart not available.</p>
            </div>
          )
          return (
            <div role="tabpanel" className="flex flex-col items-center gap-6">
              <p className="text-star-soft text-sm text-center">
                <span className="text-gold font-semibold">{activeTab} {activeTab === 'D9' ? 'Navamsa' : 'Dasamsa'}</span>
                {' '}— Ascendant: <span className="text-star-bright">{div.ascendant}</span>
              </p>
              <VedicChart planets={div.planets} ascendantSign={div.ascendant} size={380} />
              <PlanetTable planets={div.planets} />
            </div>
          )
        })()}
      </div>
    </>
  )
}

// ── View mode toggle ──────────────────────────────────────────────────────────

type ViewMode = 'summary' | 'detailed'

function getStoredView(): ViewMode {
  try { return (localStorage.getItem('birthchart-view') as ViewMode) ?? 'summary' }
  catch  { return 'summary' }
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function BirthChartPage() {
  const [viewMode, setViewMode] = useState<ViewMode>(getStoredView)

  function switchView(mode: ViewMode) {
    setViewMode(mode)
    try { localStorage.setItem('birthchart-view', mode) } catch { /* ignore */ }
  }

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['birth-chart'],
    queryFn: () => chartApi.getMyChart(),
    retry: 1,
  })

  const { data: interpData, isLoading: interpLoading } = useQuery({
    queryKey: ['chart-interpretation', data?.id],
    queryFn: () => chartApi.getInterpretation(data!.id),
    enabled: !!data?.id,
    staleTime: 1000 * 60 * 60,  // 1h — chart interpretation doesn't change
    retry: (failureCount, err) => failureCount < 1 && !isNotFoundError(err),
  })

  const { data: enrichment } = useQuery({
    queryKey: ['chart-enrichment', data?.id],
    queryFn: () => chartApi.getEnrichment(data!.id),
    enabled: !!data?.id,
    staleTime: 1000 * 60 * 60 * 24, // 24h — enrichment is stable
    retry: false, // graceful degradation: don't retry if VedAstro is down
  })

  if (isLoading) return <SkeletonBirthChart className="max-w-5xl" />
  if (!data) {
    if (isNotFoundError(error)) return <SetupPrompt message="Set up your birth details to view your Vedic birth chart." />
    return <ErrorState message="Unable to load birth chart. Please try again." onRetry={refetch} />
  }

  const chart = data

  return (
    <main className="max-w-5xl space-y-5" aria-label="Birth Chart">

      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="heading-cosmic text-xl font-semibold flex items-center gap-2">
            <Star className="w-5 h-5 text-gold" aria-hidden="true" />
            Birth Chart
            <span className="sr-only">— {viewMode === 'summary' ? 'Summary view' : 'Detailed practitioner view'}</span>
          </h1>
          <p className="text-star-muted text-sm mt-0.5 font-mono">
            {chart.lagnaSign} Lagna · {chart.moonSign} Moon · {chart.sunSign} Sun
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div
            className="flex items-center gap-1 p-1 rounded-lg bg-cosmic-dusk/80 ring-1 ring-white/5"
            role="group"
            aria-label="Chart view mode"
          >
            <button
              onClick={() => switchView('summary')}
              aria-pressed={viewMode === 'summary'}
              title="Summary — plain-language interpretation"
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-fast',
                viewMode === 'summary'
                  ? 'bg-cosmic-aurora/80 text-star-white shadow-card'
                  : 'text-star-muted hover:text-star-bright',
              )}
            >
              <LayoutList className="w-3.5 h-3.5" aria-hidden="true" />
              Summary
            </button>
            <button
              onClick={() => switchView('detailed')}
              aria-pressed={viewMode === 'detailed'}
              title="Detailed — practitioner chart"
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-fast',
                viewMode === 'detailed'
                  ? 'bg-cosmic-aurora/80 text-star-white shadow-card'
                  : 'text-star-muted hover:text-star-bright',
              )}
            >
              <BarChart3 className="w-3.5 h-3.5" aria-hidden="true" />
              Detailed
            </button>
          </div>

          <Button
            onClick={() => refetch()}
            disabled={isLoading}
            variant="ghost"
            size="sm"
            aria-label="Refresh chart data"
            className="p-2"
          >
            <RefreshCw className={cn('w-4 h-4', isLoading && 'animate-spin')} aria-hidden="true" />
          </Button>
        </div>
      </div>

      {/* View content */}
      {viewMode === 'summary' ? (
        <Card className="p-5">
          <SummaryPanel
            interp={interpData}
            isLoading={interpLoading}
            chart={chart}
            enrichment={enrichment}
          />
        </Card>
      ) : (
        <DetailedView chart={chart} enrichment={enrichment} />
      )}

      {/* Practitioner hint (only in summary mode) */}
      {viewMode === 'summary' && (
        <div className="flex items-center gap-2 caption">
          <BookOpen className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
          <span>
            For the full Vedic chart, planet positions, and divisional charts,{' '}
            <button
              onClick={() => switchView('detailed')}
              className="text-violet-glow underline underline-offset-2 hover:text-star-bright transition-colors"
            >
              switch to Detailed view
            </button>
            .
          </span>
        </div>
      )}
    </main>
  )
}
